import { z } from 'zod';
import { PageSeoSchema } from './page.schema';

// What a merchant sets about one page of their theme rather than about anything on it: how the page is
// listed in a search result, what a share card shows, and the small amount of markup a page may carry
// that no section owns. It is deliberately not a script box — see `HEAD_META_NAMES` below.
//
// The SEO half extends `PageSeoSchema`, which content pages already store in `pages.seo_metadata`, so
// the two kinds of page in this system describe themselves to a search engine in exactly one shape.

/**
 * The `<head>` tags a merchant may add to a page. A closed list, because the honest reason to want head
 * access is proving domain ownership to a search engine or an ad platform, and every other reason —
 * analytics, pixels, chat widgets — is a script tag this platform does not serve. Tracking belongs to
 * the pixel configuration (`tracking_pixels`), which knows consent and server-side forwarding; a free
 * text box here would be a second, worse copy of it that nothing could audit.
 */
export const HEAD_META_NAMES = [
  'google-site-verification',
  'facebook-domain-verification',
  'msvalidate.01',
  'yandex-verification',
  'p:domain_verify',
  'ahrefs-site-verification',
  'norton-safeweb-site-verification'
] as const;

/** A verification token as every one of those platforms issues it: an opaque unpunctuated string. */
const VERIFICATION_TOKEN_RE = /^[A-Za-z0-9._~-]{8,200}$/;

/** Share images are fetched by other people's crawlers, so they must be absolute and over http(s). */
const HTTP_URL_RE = /^https?:\/\/[^\s"'<>{}\\]+$/i;

/**
 * The shapes a page's extra markup may not contain. It is the twin of the settings grammar's own
 * `UNSAFE_RE` with the families a `<head>`/`<body>` field invites added — this is the save-time refusal,
 * not the last line: what reaches a shopper is re-serialised against the storefront's allow-list, which
 * writes every tag itself and so drops anything this pattern never thought of.
 */
const UNSAFE_HTML_RE =
  /javascript:|vbscript:|data:text\/html|<\s*\/?\s*(?:script|style|iframe|frame|object|embed|applet|portal|link|meta|base|form|svg|math|noscript|template)\b|\son[a-z]+\s*=/i;

/** How much extra markup one page may carry; the same 5000 the settings grammar allows any one string. */
export const MAX_PAGE_HTML = 5000;

/** How many verification tags one page may prove ownership with; nobody needs an eighth platform. */
export const MAX_PAGE_HEAD_TAGS = 8;

// Reports whether a merchant's markup is free of the families that script, before it is ever stored.
const isInertHtml = (value: string): boolean => !UNSAFE_HTML_RE.test(value);

/** One `<meta name= content=>` proving this store owns this domain. */
export const HeadMetaSchema = z.object({
  name: z.enum(HEAD_META_NAMES),
  content: z.string().trim().regex(VERIFICATION_TOKEN_RE, 'A verification token is 8 to 200 letters, digits, dots, dashes or underscores')
}).strict();

/** How this page is listed and shared: the content page's own SEO shape, plus the card image it has no column for. */
export const ThemePageSeoSchema = PageSeoSchema.extend({
  shareImage: z.string().trim().max(2048).regex(HTTP_URL_RE, 'A share image must be an http(s) URL').nullable().optional()
});

/** Everything one page of a store's theme carries about itself. */
export const ThemePageSettingsSchema = z.object({
  /** What this store calls the page in the builder's picker; empty means the theme's own name stands. */
  name: z.string().trim().max(60).default(''),
  seo: ThemePageSeoSchema.default({}),
  head: z.array(HeadMetaSchema).max(MAX_PAGE_HEAD_TAGS).default([]),
  /** Markup appended to this page's body, drawn through the storefront's allow-list like any other merchant HTML. */
  bodyHtml: z.string().max(MAX_PAGE_HTML).refine(isInertHtml, 'Page HTML may not contain a script, a style, an embedded frame or an event handler').default('')
}).strict();

export type HeadMetaDto = z.infer<typeof HeadMetaSchema>;
export type ThemePageSeoDto = z.infer<typeof ThemePageSeoSchema>;
export type ThemePageSettingsDto = z.infer<typeof ThemePageSettingsSchema>;
